import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import ApiError from './ApiError'
import { InvoiceInformationItem, InvoiceInformationMutation, InvoiceInformationResponse } from 'utils/invoiceInformation';
import { InvoiceDispute, InvoiceDisputeContext, InvoiceDisputeProposal, InvoiceDisputeReceipt, InvoiceDisputeReview } from 'utils/invoiceDispute';
import { getAcquisitionSnapshotPayload } from '../utils/marketingAttribution';
import { handlePhoneGateResponse } from '../utils/phoneVerificationGate';
import autoBind from 'auto-bind'
import dayjs from "dayjs";

import { mapJobTypeToPosition } from 'utils'
import { Location } from 'components/BillingLocationSelector'
import { BackendJob } from 'components/EditJob'
import { Application } from 'components/JobBoardDetails'
import { NewLocation } from 'components/AddOfficeLocation'
import { SavedCard } from "components/PurchaseTokensFlow";
import { CandidateStats } from '.'
import { EditedJob, formatEditJobBackend, HygienistBackendJob } from 'components/EditJob'
import * as ls from "utils/localStorage";
import {
	InvoiceProps,
	InvoicePayNowQuote,
	PositionProps,
	Type,
	UserAccountProps,
	SkillsProps,
	HygienistScoreProps,
	BasicHygienistScoreProps,
	FiltersProps,
	JOB_SEEKING_STATUS,
	MembershipStatusResponse,
} from "utils/types";
import {JobDataProps} from "../pages/dentist/PostJobPage/PostJob";
import { HygienistSkill } from 'utils/constants';
import {
	clearCreateRequestKey,
	clearJobPostRequestKey,
	getOrCreateCreateRequestKey,
	getOrCreateJobPostRequestKey,
} from './createRequestKey';
import type {
	CeDirectoryQuery,
	CeDirectoryResponse,
	CeDirectorySourceSurface,
} from '../features/ceDirectory/types';

export interface WritingAssistFeedbackRequest {
	assistReference: string;
	feedback: 'up' | 'down';
}

export interface ClientFeatureFlagsResponse {
	aiWritingAssistV2: boolean;
	ce_courses_directory?: boolean;
}

export interface JobAlertPreferences {
	hygienistId: number;
	enabled: 0 | 1;
	smsEnabled: 0 | 1;
	emailEnabled: 0 | 1;
	pushEnabled: 0 | 1;
	inAppEnabled: 0 | 1;
	radiusKm: number;
	daysOfWeek: string;
	basisPermanent: 0 | 1;
	basisMaternity: 0 | 1;
	basisContract: 0 | 1;
	smsConsentAt: string | null;
}

interface ProfilePreviewData { 
	applicationId: number;
	jobApplicationStatus?: number;
	interviewStatus?: number;
	showContacts?: boolean;
	applicationCreatedOn?: string;
	resumeViewedOn?: string; 
	resumeViewed: boolean; 
	hygienistId: number;
	firstName?: string; 
	lastName?: string;  
	email?: string | null; 
	phone?: string | null; 
	address?: any | null; 
	postalCode?: string | null;
	CDHONumber?: string | null; 
	city?: string; 
	province?: string; 
	country?: string; 
	photoUrl?: string;
	about?: string;
	graduationYear?: number;
	/** T2-6634: year first licensed/certified when applicable. */
	firstLicensedYear?: number | null;
	/** T2-6959: month (1-12) first licensed/certified when applicable. */
	firstLicensedMonth?: number | null;
	schoolName?: string; 
	hygienistType?: number; 
	isOrtho?: boolean;
	isRestorative?: boolean;
	starScore?: number;
	completedJobs?: number;
	ratingsNum?: number;
	skills: SkillsProps[];
	workExperience: PositionProps[];
	hygienistStatus?: number; 
	membershipStatusCode?: string | null;
	memberSince?: string; 
	isLeftHanded?: boolean; 
	recEngineScore?: number;
	location?: {
		practiceName?: string;
		city?: string;
		province?: string;
	};
}

export interface RegionalFreePeriodEligibility {
	eligible: boolean;
	province: string;
	region: string;
	campaignId: number | null;
	campaignName: string | null;
	startDate: string | null;
	endDate: string | null;
	placementFeeWaived: boolean;
	tokensWaived: boolean;
	eligibilityBasis: 'shift_date' | 'resume_view_date';
	referenceDate: string | null;
	reason: 'eligible' | 'reference_date_required' | 'outside_campaign' | 'no_campaign';
	status: 'scheduled' | 'active' | 'expired' | 'inactive' | null;
	customerFacingMessage: string | null;
	customerFacingEndDate: string | null;
	timezone: string;
	timezoneSource: 'location' | 'region_fallback';
}

interface ProfilePreviewResponse {
	profileData: ProfilePreviewData;
	isTokenRequiredForFullView: boolean;
	reasonNoTokenRequired?: string | null;
	tokensRemaining: number;
	freePeriod?: RegionalFreePeriodEligibility | null;
}

type FullProfileData = ProfilePreviewData;

// --- Added Talent Review Types ---
interface ReviewData {
	jobId: number;
	locationId: number;
	reviewSolicitationId?: string;
	overallRating?: number;
	collaborationPreference: 1 | 2 | 3 | null;
	friendlinessRating?: number | null;
	friendlinessComments?: string;
	infectionControlRating?: number | null;
	infectionControlComments?: string;
	equipmentRating?: number | null;
	equipmentComments?: string;
	preparationRating?: number | null;
	preparationComments?: string;
	additionalFeedback?: string;
	comments?: string;
}

interface TalentReview {
	id: number;
	jobId: number;
	hygienistId: number;
	locationId: number;
	friendlinessRating: 1 | 2 | 3 | 4 | 5 | null;
	friendlinessComments?: string | null;
	infectionControlRating: 1 | 2 | 3 | 4 | 5 | null;
	infectionControlComments?: string | null;
	equipmentRating: 1 | 2 | 3 | 4 | 5 | null;
	equipmentComments?: string | null;
	preparationRating: 1 | 2 | 3 | 4 | 5 | null;
	preparationComments?: string | null;
	collaborationPreference: CollaborationPreference | null;
	additionalFeedback?: string | null;
	comments: string | null;
	/** Overall 1–5 may live on Job.dentistRating; included when API joins it. */
	overallRating?: number | null;
	reviewDate: Date; // Assuming backend sends a string convertible to Date
	submissionStatus: SubmissionStatus | null;
	accepted?: boolean;
	supportDelivery?: {
		status: 'pending' | 'accepted' | 'failed' | 'unknown';
		eventId: string;
	};
}

enum SubmissionStatus {
	Deferred = 0,
	Submitted = 1,
	Dismissed = 2
}

enum CollaborationPreference {
	WOULD_LOVE_TO_WORK_AGAIN = 1,
	FINE_BUT_NOT_FAVOURITE = 2,
	PREFER_NOT_TO_WORK_AGAIN = 3
}

interface PendingReview {
	jobId: number;
	locationId: number;
	startDate: string;
	endDate: string;
	officeName: string;
}

export interface Language {
	id: number;
	code: string;
	name: string;
	native_name: string;
} 
// --- End Added Talent Review Types ---

// Rate Selector Configuration Response
export interface RateSelectorConfigResponse {
	bottomRateMultiplier: number;
	baseTopRateMultiplier: number;
	newGradThresholdYears: number;
	newGradAdjustment: number;
	newGradColorCompression: number;
	statusAdjustments: Record<number, number>;
	colorThresholds: Record<number, { greenMax: number; yellowMax: number }>;
	orangeCount: number;
	maxDeltaCap: number;
	configVersion: number;
}

export interface RateBenchmarkResponse {
	suggestedMin: number;
	suggestedRate: number;
	suggestedMax: number;
	medianBookedRate: number;
	sampleSize: number;
	lastUpdated: string;
	guidanceAvailable?: boolean;
	recommendedRate?: number;
	lowMaxRate?: number;
	strongMinRate?: number;
	activeTopAtRecommendedRate?: number;
	typicalBookedRate?: number;
	p90BookedRate?: number;
	p95BookedRate?: number;
	guidanceContext?: {
		province: string;
		postalPrefix: string;
		jobType: number;
		dayType: 'weekday' | 'saturday' | 'sunday_or_holiday';
		leadTimeBucket: 'same_day' | 'next_day' | '2_7d' | '8d_plus';
		guidanceSource: string;
		guidanceGranularity?: 'local_exact' | 'local_broader' | 'province_exact' | 'province_broader' | 'regional_fallback';
		guidanceConfidence?: 'high' | 'medium' | 'low';
		matchedPostalPrefix?: string;
		matchedDayType?: 'weekday' | 'saturday' | 'sunday_or_holiday' | 'fallback';
		matchedLeadTimeBucket?: 'same_day' | 'next_day' | '2_7d' | '8d_plus' | 'fallback';
		minimumSampleSize?: number;
		fallbackReason?: 'missing_segment' | 'insufficient_sample_size';
		policyAdjustment?: {
			type: string;
			weekdayRecommendedRate: number;
			recommendedRateFloor: number;
			appliedDelta: number;
		};
		modelVersion: string;
	};
}

export type ManualShiftPayload = {
	locationId: number;
	shiftDate: string; // YYYY-MM-DD
	startTime: number; // Unix timestamp (seconds)
	endTime: number;   // Unix timestamp (seconds)
	hourlyRate: number;
};

export type ManualShiftResponse = {
	jobId: number;
	locationId: number;
	hygienistId: number;
	shiftDate: string;
	startTime: number; // Unix timestamp (seconds)
	endTime: number;   // Unix timestamp (seconds)
	hourlyRate: number;
};

export const WRITING_ASSIST_CONTEXTS = [
	'aboutMe',
	'baseCoverLetter',
	'applicationCoverLetter',
	'jobDescription',
] as const;

export type WritingAssistContext = typeof WRITING_ASSIST_CONTEXTS[number];
export type WritingAssistScope = 'talent' | 'location';
export type WritingAssistViolationCategory =
	| 'contact_phone'
	| 'contact_email'
	| 'contact_url'
	| 'contact_social';
export type WritingAssistRefusalCategory =
	| 'offsite_steering'
	| 'competitor'
	| 'disparagement'
	| 'toxic'
	| 'incoherent';
export type WritingAssistCoachDimension =
	| 'spelling'
	| 'grammar'
	| 'too_short'
	| 'too_long'
	| 'missing_content'
	| 'clarity';

export interface WritingAssistRequest {
	context: WritingAssistContext;
	text: string;
	entityId?: number;
}

export interface WritingAssistUsage {
	scope: WritingAssistScope;
	used: number;
	max: number;
	resetsAt: string;
}

export interface WritingAssistSuccessResponse {
	policy: {
		blocking: false;
		violations: Array<{ category: WritingAssistViolationCategory; excerpt: string }>;
	};
	tidied: string;
	enhanced: string;
	coach: {
		observations: import('components/WritingAssistMoments/WritingAssistMoments.types').CoachObservation[];
	};
	hashes: { tidied: string; enhanced: string };
	assists: WritingAssistUsage;
	assistReference?: string;
}

export interface WritingAssistRefusalResponse {
	refusal: { category: WritingAssistRefusalCategory; message: string };
	assists: WritingAssistUsage;
}

export type WritingAssistResponse = WritingAssistSuccessResponse | WritingAssistRefusalResponse;

export type ManualBookShiftError =
  | { type: 'INVALID_LOCATION' }
  | { type: 'INVALID_HYGIENIST' }
  | { type: 'INVALID_DATE' }
  | { type: 'INVALID_SHIFT_TIME' }
  | { type: 'DATE_ALREADY_BOOKED' }
  | { type: 'INVALID_DENTIST' }
  | { type: 'DENTIST_ALREADY_BOOKED' }
  | { type: 'BEFORE_REGISTER_DATE'; registrationUnixTimestamp: number }
  | { type: 'UNEXPECTED_ERROR' }
  | { type: 'INTERNAL_SERVER_ERROR' }
;

/** T2-7517: shift-list requests made in the background (Home calendar, refresh on focus). */
const backgroundPhoneGateUrls = new Set<string>();

export default class Api {
	baseUrl: string
	commonHeaders: { [key: string]: string }
	authorizationHeader: string

	// In-flight request coalescing for GET /v3/tsusers/me. Many independent
	// hooks/effects on the Talent/DO home page call Api.getUser() during the
	// same render cycle; without dedupe this produced 10+ identical requests
	// (each of which fans out into ~5 MySQL queries on a remote RDS). We share
	// a single promise until it resolves, then clear it so subsequent calls
	// (e.g. after login/role change) still hit the network.
	private _getUserInFlight: Promise<UserAccountProps> | null = null

	constructor(baseUrl: string, authorizationHeader: string) {
		this.baseUrl = baseUrl
		this.authorizationHeader = authorizationHeader

		this.commonHeaders = {
			Accept: 'application/json',
			'content-type': 'application/json',
		}

		autoBind(this)
	}

	private async request(url: string, options: RequestInit = {}) {
		const fullUrl = `${this.baseUrl}${url}`;
		// Directory queries can contain member-entered search text and office
		// Location context. Keep those values out of browser/WebView logs while
		// retaining the endpoint-level request diagnostic.
		const loggedUrl = url.startsWith('v3/ce/listings?')
			? `${this.baseUrl}v3/ce/listings`
			: fullUrl;
		const cordova = (window as any).cordova;
		const runtime = cordova && cordova.platformId;
		const platform = runtime === 'ios' || runtime === 'android' ? runtime
			: (cordova && runtime !== 'browser' ? 'unknown' : 'web');
		console.log('Api.request - fullUrl:', loggedUrl);
		const res = await fetch(fullUrl, {
			credentials: 'include',
			mode: "cors",
			...options,
			headers: {
				...this.commonHeaders,
				...options.headers,
				'Authorization': this.authorizationHeader,
				'X-App-Platform': platform,
			},
		})
		if (
			res.status.toString().startsWith('4') ||
			res.status.toString().startsWith('5')
		) {
			const errorText = await res.text();
			
			// Only handle TRUE session expiry (password change) - NOT other 401 errors
			// The SESSION_EXPIRED code is specifically set by the backend when tokenVersion mismatches
			// Other 401 errors (permission denied, invalid token, etc.) should NOT trigger logout
			if (res.status === 401) {
				const isSessionExpired = this.isSessionExpiredError(errorText);
				if (isSessionExpired) {
					this.handleSessionExpiry(errorText);
				}
			}
			// T2-7517: "phone not verified" (403) opens the SMS verification flow
			// instead of failing silently; background loads respect "later".
			if (res.status === 403) {
				handlePhoneGateResponse(res.status, errorText, { background: backgroundPhoneGateUrls.has(url) });
			}
			
			throw new ApiError(
				res.status,
				errorText,
			)
		}
		if (res.status === 204) {
			return res
		}
		const contentType = res.headers.get('content-type') || '';
		if (contentType.includes('application/json')) {
			return res.json()
		}
		// Fallback for binary (e.g., PDFs)
		return res.arrayBuffer()
	}

	/** Returns true if error is SESSION_EXPIRED (password change), not other 401s */
	private isSessionExpiredError(errorText: string): boolean {
		try {
			const errorJson = JSON.parse(errorText);
			
			if (errorJson?.error?.code === 'SESSION_EXPIRED') {
				return true;
			}
			
			// Fallback: check message for backwards compatibility
			const message = errorJson?.error?.message?.toLowerCase() || '';
			return message.includes('session expired') && message.includes('password');
		} catch {
			return false;
		}
	}

	/** Shows session expired modal and redirects to login */
	private handleSessionExpiry(errorText: string): void {
		// Use a global window flag to prevent multiple alerts if several API calls fail simultaneously
		// This flag is shared between Api.ts (React) and ajax.js (legacy) code paths
		// @ts-ignore
		if (window.__tempstars_handling_session_expiry) {
			return;
		}
		// @ts-ignore
		window.__tempstars_handling_session_expiry = true;
		
		// Access Framework7's app object and TempStars namespace
		// @ts-ignore
		const app = window.app;
		// @ts-ignore
		const mainView = window.mainView;
		// @ts-ignore
		const TempStars = window.TempStars;
		
		// CRITICAL: Hide any active preloader before showing the session expiry modal
		// This ensures the modal is visible and clickable (not blocked by preloader)
		// This is especially important when 401 occurs during autoLogin's Promise.all()
		// where error handling might be swallowed by individual catch blocks
		if (app && typeof app.hidePreloader === 'function') {
			app.hidePreloader();
		}
		
		// Since we now only call this for SESSION_EXPIRED (tokenVersion mismatch),
		// we can use a clear message about password change
		const message = 'Your session has expired due to a password change.<br>Please log in with your new password.';
		
		// Use TempStars.User.logout() for complete cleanup if available
		const doLogoutAndRedirect = async () => {
			try {
				if (TempStars && TempStars.User && typeof TempStars.User.logout === 'function') {
					await TempStars.User.logout();
				} else {
					// Fallback: clear storage manually
					ls.remove('userAuth');
					ls.remove('userAccount');
					ls.remove('persist:root');
					ls.remove('pageData');
				}
			} catch (e) {
				console.error('Error during session expiry cleanup:', e);
				// Even if logout fails, continue with cleanup
				ls.remove('userAuth');
				ls.remove('userAccount');
				ls.remove('persist:root');
				ls.remove('pageData');
			}
			
			// Navigate to login page using React router (with small delay to allow cleanup to complete)
			// This matches the pattern used in TempStars.Menu.logout()
			setTimeout(() => {
				if (TempStars && TempStars.App && typeof TempStars.App.goReact === 'function') {
					TempStars.App.goReact('Login');
				} else {
					// Fallback: reload page if TempStars.App not available
					window.location.reload();
				}
				
				// Reset global flag after navigation
				// @ts-ignore
				window.__tempstars_handling_session_expiry = false;
			}, 500);
		};
		
		// Show user-friendly alert and redirect to login when OK is clicked
		if (app && typeof app.alert === 'function') {
			app.alert(message, 'Session Expired', () => {
				doLogoutAndRedirect();
			});
		} else {
			// Fallback for when Framework7 app is not available
			alert(message.replace(/<br>/g, '\n'));
			doLogoutAndRedirect();
		}
	}

	private get(url: string) {
		console.log('Api.get called with url:', url);
		return this.request(url, {
			method: 'GET',
		})
	}

	private delete(url: string, body?: object) {
		return this.request(url, {
			method: 'DELETE',
			body: body
				? JSON.stringify(body)
				: undefined,
		})
	}

	private post(url: string, body?: object) {
		return this.request(url, {
			method: 'POST',
			body: body
				? JSON.stringify(body)
				: undefined,
		})
	}

	private put(url: string, body?: object) {
		return this.request(url, {
			method: 'PUT',
			body: body
				? JSON.stringify(body)
				: undefined,
		})
	}

	async submitWritingAssistFeedback(payload: WritingAssistFeedbackRequest): Promise<void> {
		await this.post('v3/ai/writing-assist/feedback', payload);
	}

	async updateToken() {
		const token = ls.get('userAuth') as {jwtToken: string};
		this.authorizationHeader = `Bearer ${token.jwtToken}`
	}

	async getAllHireJobs(): Promise<Array<BackendJob>> {
		const backendHireJobs: Array<any> = await this.get('v3/hireJobs')

		return backendHireJobs
			.map((
				{
					jobType,
					new_applications,
					status,
					...props
				},
			) => ({
				position: mapJobTypeToPosition(jobType),
				jobType,
				newCandidates: new_applications,
				status,
				...props,
			}))
	}

	getAvailableHireJobs(): Promise<Array<HygienistBackendJob>> {
		return this.get('v3/hireJobs')
	}

	getJobAlertPreferences(): Promise<JobAlertPreferences> {
		return this.get('v3/hireJobs/alertPreferences')
	}

	updateJobAlertPreferences(preferences: Partial<JobAlertPreferences> & { smsConsent?: boolean }): Promise<JobAlertPreferences> {
		return this.put('v3/hireJobs/alertPreferences', preferences)
	}

	recordJobAlertInteraction(event: string, properties: Record<string, unknown> = {}): Promise<void> {
		return this.post('v3/hireJobs/alertPreferences/interaction', { event, properties })
	}

	async getPublicHireJobByIdForHygienist(jobId: number): Promise<HygienistBackendJob> {
		return this.get(`v3/hireJobs/hygienist/public/${jobId}`);
	}

	async getHireJob(jobId: number): Promise<BackendJob> {
		const hireJob = await this.get('v3/hireJobs/' + jobId)
		return {
			...hireJob,
			position: mapJobTypeToPosition(hireJob.jobType)
		}
	}

	// -------- Featured Listing (Job Board) --------
	async getFeatureAvailability(hireJobId: number): Promise<{ status: 'available' | 'queued' | 'unavailable'; activeCount: number; jobType: number }> {
		return this.get(`v3/hireJobs/${hireJobId}/feature/availability`);
	}

	async featureListing(hireJobId: number): Promise<{ status: 'in_rotation' | 'queued'; message: string; featuredListingId?: number }> {
		return this.post(`v3/hireJobs/${hireJobId}/feature/enable`);
	}

	async getFeatureStatus(hireJobId: number): Promise<{ isFeatured: boolean; status: string }> {
		return this.get(`v3/hireJobs/${hireJobId}/feature/status`);
	}

	async unfeatureListing(hireJobId: number): Promise<void> {
		return this.delete(`v3/hireJobs/${hireJobId}/feature`);
	}

	async trackRecruitNAClick(hireJobId: number, linkUrl?: string): Promise<void> {
		// Pass the URL actually bound to the clicked link so Mixpanel `link_url`
		// matches the destination (T2-6420). Backend falls back to the configured
		// setting / default when linkUrl is omitted.
		return this.post(`v3/hireJobs/${hireJobId}/trackRecruitNAClick`, { linkUrl });
	}

	// T2-6420: admin-configurable Recruit North America landing URL shown on the
	// Job Board post-success screen. Read access is dental-office gated.
	async getJobBoardPartnerLink(): Promise<{ url: string }> {
		return this.get(`v3/config/jobBoardPartnerLink`);
	}

	// T2-7017: public Google Maps/Business review count shown before login.
	async getGoogleReviews(): Promise<{ totalReviews: number }> {
		return this.get(`v3/config/googleReviews`);
	}

	getClientFeatureFlags(signal?: AbortSignal): Promise<ClientFeatureFlagsResponse> {
		return this.request('v3/feature-flags/client', {
			method: 'GET',
			cache: 'no-store',
			signal,
		});
	}

	getCeDirectoryListings(
		query: CeDirectoryQuery,
		signal?: AbortSignal,
	): Promise<CeDirectoryResponse> {
		const params = new URLSearchParams({
			group: query.group,
			format: query.format,
			scope: query.scope,
			page: String(query.page ?? 1),
		});
		const search = query.q?.trim();
		if (search) params.set('q', search);
		if (query.locationId != null) params.set('locationId', String(query.locationId));
		return this.request(`v3/ce/listings?${params.toString()}`, {
			method: 'GET',
			cache: 'no-store',
			signal,
		});
	}

	createCeListingClick(
		listingId: number,
		sourceSurface: CeDirectorySourceSurface,
		locationId?: number,
	): Promise<{ url: string }> {
		return this.post(`v3/ce/listings/${listingId}/click`, {
			sourceSurface,
			...(locationId != null ? { locationId } : {}),
		});
	}

	async markJobFilled(hireJobId: number): Promise<{ success: boolean; message: string }> {
		return this.post(`v3/hireJobs/${hireJobId}/mark-filled`, {});
	}

	async getApplicationsForHireJob(jobId: number): Promise<Array<Application>> {
		const backendCandidates: Array<any> = await this.get(`v3/hireJobs/${jobId}/applications`)
		return backendCandidates
			.map(
				({
					 id: applicationId,
					 hygienistId,
					 status,
					 resumeViewed,
					 hygienist: {
						 graduationYear,
						 firstLicensedYear,
						 firstLicensedMonth,
						 school,
						 starScore,
						 firstName,
						 lastName,
						 city,
						 province,
						 photoUrl,
						 isReliable,
						 ...hygienistProps
					 },
					 user: {
						 ...userProps
					 },
					 ...props
				 }) => {
					const yearsOfExperienceValue = dayjs().diff(`${graduationYear}-01-01`, 'years');
					let candidateName = 'Candidate';

					if (resumeViewed && firstName && lastName) {
						candidateName = `${firstName} ${lastName}`;
					} else if (!resumeViewed && firstName && lastName) {
						candidateName = `${firstName.charAt(0)}.${lastName.charAt(0)}.`;
					} else if (!resumeViewed && firstName) {
						candidateName = `${firstName.charAt(0)}.`;
					}

					return {
						...props,
						...hygienistProps,
						...userProps,
						applicationId,
						hygienistId,
						status,
						resumeViewed,
						name: candidateName,
						firstName, // Include firstName (will be initials when not viewed)
						lastName,  // Include lastName (will be initials when not viewed)
						hygienistStatus: hygienistProps.status,
						membershipStatusCode: props.membershipStatusCode ?? hygienistProps.membershipStatusCode ?? null,
						school,
						city,
						province,
						graduationYear,
						firstLicensedYear: firstLicensedYear ?? null,
						firstLicensedMonth: firstLicensedMonth ?? null,
						photoUrl,
						graduation: `Graduated ${graduationYear} from ${school}`,
						yearsOfExperience: `${yearsOfExperienceValue} ${yearsOfExperienceValue === 1 ? 'year' : 'years'} experience`,
						rating: starScore,
						id: undefined,
					}
				})
	}

	getJobDetails(jobId: number): Promise<any> {
		console.log('Api.getJobDetails called with jobId:', jobId, 'type:', typeof jobId);
		// Ensure jobId is a clean number
		const cleanJobId = parseInt(String(jobId), 10);
		if (isNaN(cleanJobId)) {
			console.error('Invalid jobId:', jobId);
			throw new Error('Invalid job ID');
		}
		console.log('Clean jobId:', cleanJobId);
		// Try different URL construction methods to isolate the issue
		const url1 = `v3/jobs/${cleanJobId}/details`;
		const url2 = 'v3/jobs/' + cleanJobId + '/details';
		const url3 = ['v3/jobs', cleanJobId, 'details'].join('/');
		console.log('URL construction test:');
		console.log('Template literal:', url1);
		console.log('String concat:', url2);
		console.log('Array join:', url3);
		// Use string concatenation instead of template literal
		return this.get('v3/jobs/' + cleanJobId + '/details');
	}

	getJobChatMessages(jobId: number): Promise<{
		messages: Array<{
			id: number;
			authorUserId: number;
			authorType: string;
			authorName: string;
			body: string;
			sentAt: string;
			twilioMessageSid?: string | null;
		}>;
	}> {
		return this.get(`v3/twilio/channel/${jobId}/messages`);
	}

	getHygienistStatsById(props: { hygienistId: number, applicationId: number }): Promise<CandidateStats> {
		const url = `v3/hygienists/${props.hygienistId}/statsForJobBoard?applicationId=${props.applicationId}`;
		return this.get(url);
	}

	getHygienistOfficeHistory(hygienistId: number, locationId: number): Promise<{ completedAtLocation: number; lastRating: number | null }> {
		return this.get(`v3/hygienists/${hygienistId}/office-history?locationId=${locationId}`);
	}

	getHygienistLocationDetails(hygienistId: number): Promise<{ lat: string; lon: string } | null> {
		return this.get(`v3/hygienists/${hygienistId}/location-details`);
	}

	purchaseResume(applicationId: number): Promise<any> {
		return this.put(`v3/jobApplications/${applicationId}/viewResume`)
	}

	checkRecentlyViewed(applicationId: number): Promise<any> {
		return this.get(`v3/jobApplications/${applicationId}/checkRecentlyViewed`)
	}

	async updateApplication({ applicationId, newData }: { applicationId: number, newData: any }): Promise<any> {
		await this.put(`v3/jobApplications/${applicationId}`, newData)
		return newData
	}

	async isPhoneNumberUnique(phone: number): Promise<boolean> {
		return this.get(`v3/locations/check?phone=${phone}`);
	}

	async getLocations(): Promise<Array<Location>> {
		let backendLocations: Array<any> = await this.get('v3/locations')
		backendLocations = backendLocations.filter((location: any) => location.removed === 0);
		return backendLocations.map(({ id, address, card, completedJobsCount, ccStatus, practiceName, ...props }: any) => ({
			...props,
			id,
			address,
			practiceName,
			completedJobsCount: typeof completedJobsCount === 'number' ? completedJobsCount : 0,
			ccStatus: (ccStatus as 'ACTIVE' | 'FAILED' | 'CANCELLED') || (props?.isCardValid ? 'ACTIVE' : 'CANCELLED'),
			hasCard: !!card,
			card,
		}))
	}

	async getValidLocations(): Promise<Array<Location>> {
		let backendLocations: Array<any> = await this.get('v3/locations')
		// Always exclude removed locations (T2-6732). The previous OR filter
		// re-included removed rows when deactivated===1 or isCardValid===0.
		backendLocations = backendLocations.filter((location: any) => location.removed === 0)
		return backendLocations
			.map(
				({
					 id,
					 address,
					 card,
					 ...props
				 }) => ({
					...props,
					id,
					address,
					hasCard: !!card,
					card,
				}))
	}

	async getPromotionEligibilityStatus(locationId: number): Promise<{
		eligible: boolean;
		reason: string;
		daysRemaining: number;
		promotionEndDate: string | null;
	}> {
		return this.get(`v3/promotions/eligibility/${locationId}`);
	}

	async getRegionalFreePeriodEligibility(
		locationId: number,
		date?: string | null,
		benefit: 'placement' | 'tokens' = 'placement',
	): Promise<RegionalFreePeriodEligibility> {
		const query = new URLSearchParams({ benefit });
		if (date) query.set('date', date);
		return this.get(`v3/locations/${locationId}/regional-free-period?${query.toString()}`);
	}

	async cardById(customerId: string, country: number): Promise<SavedCard | null> {
		const cards: {data: {last4: any, brand: string}[] | null} = await this.post(`v3/locations/cards`, {customerId, country})
		const defaultCard = cards?.data?.length && cards?.data[0] || null

		return defaultCard ? {
			last4: defaultCard.last4,
			type: defaultCard.brand.toLowerCase(),
			} : null
	}

	// Payment Info/Stripe helpers for Dentist Locations parity
	revalidateCard(
		locationId: number,
		paymentPurpose?: 'revenue' | 'native' | 'country',
	): Promise<{ client_secret: string }> {
		return this.post(`v3/stripe/revalidateCard`, { locationId, paymentPurpose })
	}

	updateCardStatus(params: {
		locationId: number,
		isCardValid: number,
		paymentPurpose?: 'revenue' | 'native' | 'country',
	}): Promise<any> {
		// Legacy-compatible endpoint expects locationId in body, not path param
		return this.put(`v3/locations/updateCardStatus`, params)
	}

	getDefaultSource(
		locationId: number,
		paymentPurpose?: 'revenue' | 'native' | 'country',
	): Promise<any> {
		return this.post(`v3/stripe/getDefaultSource`, { locationId, paymentPurpose })
	}

	/**
	 * Create a dental office location. Pass only step-1 fields for minimal creates, or include
	 * practice/operational fields for a single-shot full profile (e.g. Add Location step 2 submit).
	 */
	addLocationV2(params: {
		address: string;
		city: string;
		province: string;
		postalCode: string;
		country: number;
		phone: string;
		businessOwner: string;
		email?: string;
		practiceName?: string;
		secondaryEmail?: string;
		accountingEmail?: string;
		primaryContact?: string;
		parking?: string;
		radiography?: string;
		ultrasonic?: string;
		avgApptTime?: string;
		charting?: string;
		software?: string;
		isUpdated?: number;
	}): Promise<object> {
		const body = Object.fromEntries(
			Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
		) as Record<string, string | number>;
		const idempotencyKey = getOrCreateCreateRequestKey('location-add', body);
		return this.request('v3/locations', {
			method: 'POST',
			headers: { 'X-Idempotency-Key': idempotencyKey },
			body: JSON.stringify(body),
		}).then((result) => {
			clearCreateRequestKey('location-add', body);
			return result;
		});
	}

	addLocation(location: NewLocation): Promise<object> {

		return this.post('v3/locations', {
			address: location.streetAddress,
			city: location.unit,
			email: location.email,
			practiceName: location.unit,
			province: location.province,
			postalCode: location.zipCode,
			country: 0, // CANADA!!!
			scentFreePolicy: location.scentFreePolicy,
		})
	}

	deleteLocation(locationId: number): Promise<object> {
		return this.delete(`v3/locations/${locationId}`)
	}

	updateLocation(params: { locationId: number, data: any }): Promise<any> {
		return this.put(`v3/locations/${params.locationId}/info`, params.data);
	}

	updatePaymentInfo(
		locationId: number,
		options: boolean | { isRevalidation?: boolean; paymentPurpose?: 'revenue' | 'native' | 'country' } = false,
	): Promise<object> {
		const isRevalidation = typeof options === 'boolean' ? options : !!options.isRevalidation;
		const body = typeof options === 'boolean'
			? {}
			: (options.paymentPurpose ? { paymentPurpose: options.paymentPurpose } : {});
		const url = isRevalidation
			? `v3/locations/${locationId}/paymentInfo?isRevalidation=true`
			: `v3/locations/${locationId}/paymentInfo`;
		return this.put(url, body)
	}

	deleteJobPosting(data: { jobPostingId: number, answer: string, feedback: string }) {
		return this.put(`v3/hireJobs/${data.jobPostingId}/cancelJob`, { answer: data.answer, feedback: data.feedback })
	}

	purchaseTokens(
		{
			locationId,
			tokensAmount,
		}: { locationId: number, tokensAmount: number },
	): Promise<undefined> {
		return this.post(`v3/hireJobs/purchaseTokens`, {
			locationId,
			tokensAmount,
		})
	}

	createUser({ email, password, affiliateCode}: { email: string, password: string, affiliateCode: string}): Promise<{ userId: number }> {
		let payload: any = { email, password, affiliateCode, acquisition: getAcquisitionSnapshotPayload() };
		
		try {
			const referralData = safeLocalStorage.getItem('partner_referral');
			if (referralData) {
				const parsed = JSON.parse(referralData);
				// Check if not expired
				if (new Date(parsed.expires_at) > new Date()) {
					payload.partnerReferralSource = parsed.partner_referral_source;
				}
			}
		} catch (error) {
			console.error('[Partner Tracking] Error reading referral data:', error);
		}
		
		return this.post('v3/tsusers/createUser', payload)
	}

	checkUniqueHygienistPhoneNumber({ phone, type }: { phone: number, type: number }) {
		return this.post('v3/hygienists/check/phoneNumber', { phone, type })
	}

	async checkUniqueHygienistCDHONumber({ CDHONumber, type, province }: { CDHONumber: number | string, type: number, province: string }) {
		return this.post('v3/hygienists/check/CDHONumber', { CDHONumber, type, province })
	}

	createUserRole({ roleId, userId }:  { roleId: number, userId: number }): Promise<{ roleMappingId: number }> {
		return this.post('v3/tsusers/createUserRole', { roleId, userId })
	}

	getUser(): Promise<UserAccountProps> {
		if (this._getUserInFlight) {
			return this._getUserInFlight
		}
		const pending = (this.get('v3/tsusers/me') as Promise<UserAccountProps>)
			.finally(() => {
				this._getUserInFlight = null
			})
		this._getUserInFlight = pending
		return pending
	}

	getRole(): Promise<{roleId: number}> {
		return this.get('v3/tsusers/me/role')
	}

	// T2-5090: the authed user's referral code, share link, and free placement credit balance.
	getReferralCode(): Promise<{ referralCode: string; shareUrl: string; freePlacementCredits: number; referredCount?: number; completedReferralCount?: number }> {
		return this.get('v3/tsusers/me/referral-code')
	}

	// T2-5090: live validation of a referral code during signup (public, rate-limited).
	// `codeType` distinguishes a user-to-user referral (returns `referrerName` for the
	// "Referred by …" message) from a partner/FREE_PLACEMENT campaign code (no referrer name).
	validateReferralCode(referralCode: string): Promise<{ valid: boolean; codeType?: 'user' | 'partner'; referrerName?: string }> {
		return this.post('v3/tsusers/validate-referral-code', { referralCode })
	}

	changeRole(role: number | null) {
		return this.put('v3/tsusers/me/changeRole', { roleId: role });
	}

	updateUser(data: any): Promise<any> {
		return this.put('v3/tsusers/me', data)
	}

	createDentist(data: any ): Promise<{ dentistId: number, locationId: number, registrationReceipt?: { id: string } }> {
		return this.post('v3/dentists', data)
	}

	cancelHireJob(jobId: number): Promise<object> {
		return this.put(`v3/hireJobs/${jobId}/cancelJob`)
	}

	postJob(editedJob: EditedJob) {
		return this.post(`v3/hireJobs`, formatEditJobBackend(editedJob))
	}

	editJob(editedJob: EditedJob) {
		return this.put(`v3/hireJobs/${editedJob.id}`, formatEditJobBackend(editedJob))
	}

	deleteJob(jobId: number) {
		return this.delete(`v3/hireJobs/${jobId}`)
	}

	async deleteUser(data?: { deleteReason?: string, details?: string | null }): Promise<any>  {
		return this.post(`v3/tsusers/me/deactivate`, data)
	}

	async uploadPDF(file: File): Promise<string> {
		const { url, signedRequest } = await this.get('v3/aws/uploadCredentials')
		await fetch(signedRequest, {
			method: 'PUT',
			headers: {
				'Content-Type': file.type,
			},
			body: file,
		})
		await this.put(`v3/hygienists/me`, {
			hireResumeUrl: url,
		})
		return url
	}

	async applyForHireJob(hireJobId: number): Promise<any> {
		return this.post(`v3/jobApplications`, {
			hireJobId,
		})
	}

	async applyForHireJobWithCoverLetter({hireJobId, coverLetter}: { hireJobId: number, coverLetter?: string | null }): Promise<any> {
		return this.post(`v3/jobApplications`, {
			hireJobId,
			coverLetter
		})
	}

	async withDrawForHireJob({ applicationId }: { applicationId: number }): Promise<any> {
		return this.delete(`v3/jobApplications/${applicationId}`)
	}

	async allowShowInfo({ ApplicationId, allow }: { ApplicationId: number, allow: boolean }): Promise<any> {
		return this.post(`v3/hirejobs/allowSeeInfo`, { ApplicationId, allow})
	}

	async getAvailableJobs(
		type: number,
		date?: string,
		locationId?: number,
		includeAdmin?: boolean,
		options?: { calendar?: boolean; background?: boolean },
	): Promise<any> {
		const search = new URLSearchParams();
		search.set('type', String(type ? 1 : 0));
		if (date) {
			search.set('date', date);
		}
		if (locationId) {
			search.set('locationId', String(locationId));
		}
		if (includeAdmin) {
			search.set('includeAdmin', '1');
		}
		if (options?.calendar) {
			search.set('calendar', '1');
		}
		// T2-7517: ask for "phone_not_verified" (403) instead of an empty list
		// when the server's verifiedPhoneShiftGate switch is in enforce.
		search.set('phoneGate', '1');
		search.set('_ts', String(Date.now()));
		const url = `v3/jobs/hygienistAvailableJobs?${search.toString()}`;
		if (!options?.background) return this.get(url);
		backgroundPhoneGateUrls.add(url);
		try {
			return await this.get(url);
		} finally {
			backgroundPhoneGateUrls.delete(url);
		}
	}

	async getMembershipStatus(): Promise<MembershipStatusResponse> {
		return this.get('v3/hygienists/me/membership-status');
	}

	async setFavouriteDentist(dentistId: number, locationId: number): Promise<any> {
		return this.post(`v3/hygienists/favouriteDentist`, { locationId, dentistId })
	}

	async removeFavouriteDentistByLocationId(locationId: number): Promise<any> {
		return this.delete(`v3/hygienists/favouriteDentist/${locationId}`)
	}

	async createSetupIntent(locationId: number, data?: { paymentPurpose?: 'revenue' | 'native' | 'country' }): Promise<any> {
		return this.post(`v3/locations/${locationId}/createSetupIntent`, data || {})
	}

	async getMarket({postalCode, name, state}: {postalCode?: string, name?: string, state?: string}): Promise<{ header: string, description: string }> {
		return this.post('v3/getMarket', {postalCode, name, state})
	}

	async createHygienist(data: any): Promise<any> {
		return this.post('v3/hygienists', { ...data, acquisition: getAcquisitionSnapshotPayload() })
	}

	async getHygienist(hygienistId: number): Promise<any> {
		return this.get(`v3/hygienists/${hygienistId}`)
	}

	async updateHygienist(data: any): Promise<any> {
		return this.put('v3/hygienists/me', data)
	}

	/** T2-6634 / T2-6959: confirm Graduation Year + First Licensed/Certified month+year (server-backed). */
	async confirmEducationCredentials(data: {
		graduationYear: number;
		firstLicensedYear?: number | null;
		firstLicensedMonth?: number | null;
		firstLicensedNotApplicable?: boolean;
	}): Promise<{
		id: number;
		graduationYear: number;
		firstLicensedYear: number | null;
		firstLicensedMonth: number | null;
		educationCredentialsConfirmedOn: string;
	}> {
		return this.put('v3/hygienists/me/education-credentials', data)
	}

	async seenPhoto(): Promise<any> {
		return this.post('v3/hygienists/me/seenPhoto')
	}

	async seenPhotoDentist(locationId?: number | null): Promise<any> {
		// T2-6085: pass the active locationId so id-list ('per-location') office
		// images are marked seen for that location only.
		return this.post('v3/dentists/me/seenPhoto', locationId ? { locationId } : {})
	}

	// T2-6085: office Photo Announce for the currently-selected location (handles
	// per-location id-list targeting + per-location 'seen').
	async getOfficePhotoAnnounce(
		locationId?: number | null
	): Promise<{ image: { id: number; photoUrl: string; announceUrl: string | null } | null }> {
		const qs = locationId ? `?locationId=${encodeURIComponent(String(locationId))}` : ''
		return this.get(`v3/dentists/me/photoAnnounce${qs}`)
	}

	async agreeToAdmin(): Promise<any> {
		return this.post('v3/hygienists/me/agreeAdmin')
	}

	async declineAdminPermanently(): Promise<any> {
		return this.post('v3/hygienists/me/declineAdmin')
	}

	async deleteHygienist(hygienistId: number): Promise<any> {
		return this.delete(`v3/hygienists/${hygienistId}`)
	}

	async updateHygienistAccount(data: any): Promise<any> {
		return this.put('v3/hygienists/me/updateAccount', data)
	}

	async updateJobSeekingStatus(jobSeekingStatus: JOB_SEEKING_STATUS): Promise<unknown> {
		return this.put('v3/hygienists/me/updateJobSeekingStatus', { jobSeekingStatus })
	}

	async updateHygienistSignup(data: any): Promise<any> {
		const result = await this.put('v3/hygienists/updateSingUp', { ...data, acquisition: getAcquisitionSnapshotPayload() });
		const receipt = result?.acquisitionReceipt;
		if (receipt?.id) {
			try { (window as any).TempStarsAttribution?.complete(window.localStorage, receipt.id); } catch (_) {}
		}
		return result;
	}

	async checkForDuplicateHygienist(hygienistId: number, firstName: string, lastName: string, userId: number, type: number, province: string): Promise<any> {
		return this.post(`v3/checkForDuplicateUser`, {hygienistId, firstName, lastName, userId, type, province})
	}

	marketingSms(data: {phoneNumber: string, platform: string, label: string}): Promise<any> {
		return this.post('v3/marketingSms', data)
	}

	login(email: string, password: string): Promise<{ token: string, isAgent?: boolean }> {
		return this.post('v3/tsusers/login', { email: email, password: password });
	}

	public setAuthJwtToken(token: string | undefined) {
		this.authorizationHeader = token ? `Bearer ${token}` : '';
		if (token) {
			ls.set('userAuth', { jwtToken: token });
		} else {
			ls.remove('userAuth');
		}
	}

	reLogin(): Promise<{ token: string, isAgent?: boolean }> {
		return this.post('v3/tsusers/me/reLogIn', {});
	}

	getSkills(data: {type: number, state: string}): Promise<HygienistSkill[]> {
		return this.post('v3/skills', data)
	}

	replaceHygienistSkills(skillIds: number[]): Promise<any> {
		return this.post('v3/skills/hygienist', { skillIds })
	}

	getHygienistSkills(hygienistId: number): Promise<any> {
		return this.get(`v3/skills/hygienist/${hygienistId}`)
	}

	getLanguages(): Promise<Language[]> {
		return this.get('v3/languages')
	}

	/**
	 * Fetch languages currently associated with the logged-in hygienist.
	 */
	getMyLanguages(): Promise<Language[]> {
		return this.get('v3/tsusers/me/languages');
	}

	addLanguages(languageIds: number[]): Promise<any> {
		return this.post('v3/tsusers/me/languages', { languageIds })
	}

	/**
	 * Remove the provided language ids from the logged-in hygienist.
	 * The backend is expected to ignore unknown ids.
	 */
	removeLanguages(languageIds: number[]): Promise<any> {
		return this.delete('v3/tsusers/me/languages', { languageIds });
	}

	/**
	 * Get region-specific language lists based on the logged-in hygienist's country
	 */
	async getRegionalLanguages(): Promise<{ country: number; common: Language[]; additional: Language[] }> {
		return this.get('v3/tsusers/me/languages/regional');
	}



	checkJobDescriptionInfo(message: string): Promise<{ spellChecked: string, final: string }> {
		return this.post('v3/jobDescriptionFilter', { message })
	}

	getDentistJobs() {
		return this.get('v3/jobs/dentistJobs');
	}

	getLocationJobs(locationId: number) {
		return this.get(`v3/jobs/${locationId}/locationJobs`);
	}

	getLocationById(locationId: number) {
		return this.get(`v3/locations/getById?locationId=${locationId}`);
	}

	getLastJob({type, locationId}: {type: Type, locationId: number}): Promise<any> {
		return this.get(`v3/jobs/lastDentistJob?type=${type}&locationId=${locationId}`);
	}

	getAvgRate({type, postalCode}:{type: Type, postalCode: string}) {
		return this.get(`v3/jobs/averageRate?jobType=${type}&postalCode=${postalCode}`)
	}

	postRegularJob(data: JobDataProps) {
		const idempotencyKey = getOrCreateJobPostRequestKey(data);
		return this.request(`v3/jobs/`, {
			method: 'POST',
			headers: { 'X-Idempotency-Key': idempotencyKey },
			body: JSON.stringify(data),
		}).then((result) => {
			clearJobPostRequestKey(data);
			return result;
		});
	}

	getAvailableHygienists({jobId}: {jobId: number}) {
		return this.get('v3/jobs/available?jobId=' + jobId)
	}

	// -------------------- Insta-Book Settings (Hygienist) --------------------

	/**
	 * Fetch hygienist Insta-Book settings snapshot
	 */
	getHygienistSettings(): Promise<{ GlobalSettings?: {
		hourlyRate: number;
		maxDistance: 25 | 50 | 75 | 110;
		minShiftLength: 4 | 5 | 6 | 7 | 8;
	}; DailySettings?: Array<{ startDate: string; start: string; end: string }>; }>{
		return this.get('v3/hygienists/me/settings');
	}

	/**
	 * Create global settings or create a new daily setting (legacy-compatible)
	 */
	setInstabookSettings(data: {
		hourlyRate?: number;
		maxDistance?: 25 | 50 | 75 | 110;
		minShiftLength?: 4 | 5 | 6 | 7 | 8;
		startDate?: string;
		start?: string;
		end?: string;
	}) {
		return this.put('v3/hygenists/instabook', data);
	}

	/**
	 * Update existing global settings
	 */
	updateInstabookSettings(data: { hourlyRate: number; maxDistance: 25 | 50 | 75 | 110; minShiftLength: 4 | 5 | 6 | 7 | 8; }) {
		return this.post('v3/hygenists/instabook', data);
	}

	/**
	 * Update/override a specific day's availability (time window)
	 */
	updateInstabookDate(data: { startDate: string; start: string; end: string; }) {
		return this.post('v3/hygenists/instabook/daily', data);
	}

	/**
	 * Remove a specific day's availability by date key
	 */
	removeInstabookDate(data: { startDate: string }) {
		const startDate = encodeURIComponent(data.startDate);
		return this.delete(`v3/hygenists/instabook?startDate=${startDate}`, data);
	}

	/**
	 * Dentist-side: Instabook a hygienist from available-now results
	 */
	instabookHygienist(params: { jobId: number; hygienistId: number }) {
		return this.post('v3/jobs/available/book', params);
	}

	/**
	 * Hygienist-side: confirm a pending Insta-Book request from an office
	 */
	confirmHygienistInstabook(params: { jobId: number }) {
		return this.post('v3/jobs/instabook/confirm', params);
	}

	/**
	 * Hygienist-side: reject a pending Insta-Book request from an office
	 */
	rejectHygienistInstabook(params: { jobId: number }) {
		return this.post('v3/jobs/instabook/reject', params);
	}

	addWorkExperience(positions: PositionProps[]) {
		return this.post('v3/tsusers/me/workExperience', {...positions})
	}

	getWorkExperience(): Promise<PositionProps[]> {
		return this.get('v3/tsusers/me/workExperience')
	}

	getHygienistWorkExperience(hygienistId: number): Promise<PositionProps[]> {
		return this.get(`v3/hygienists/workExperience/${hygienistId}`)
	}

	makePartialOffer({hourlyRate, jobId, startTime, endTime, isPartial, showJobSeekingStatus}: { hourlyRate: number, jobId: number, startTime?: string, endTime?: string, isPartial?: boolean, showJobSeekingStatus?: number }): Promise<any> {
		return this.post(`v3/offers/`, { hourlyRate, jobId, startTime, endTime, isPartial, showJobSeekingStatus })
	}

	hideOffer({jobId}: { jobId: number }): Promise<any> {
		return this.post('v3/hideJob/' + jobId, {})
	}

	unHideOffer(): Promise<any> {
		return this.post('v3/unhideJobs/', {})
	}

	makeInstabookOffer(data: {
		hourlyRate: number,
		offeredStartTime: string,
		offeredEndTime: string,
		jobId: number,
		startDate: string,
		available: boolean
	}): Promise<{ offerId: number }> {
		return this.put('v3/jobs/instabook', data)
	}

	confirmInstabook(data: {offerId: number, jobId: number} ): Promise<any> {
		return this.post('v3/jobs/instabook', data)
	}

	revertInstabook(data: {offerId: number, jobId: number} ): Promise<any> {
		return this.delete('v3/jobs/instabook', data)
	}

	getDistanceMatrix(destination: {lat: number, lng: number} ): Promise<{distance: any, duration: any}> {
		return this.post('v3/locations/getDistanceMatrix', { destination })
	}

	completeJob(jobId: number): Promise<any> {
		return this.put(`v3/jobs/${jobId}/complete`)
	}

	resubmitOffer(offerId: number): Promise<any> {
		return this.put(`v3/offers/${offerId}/resubmit`)
	}

	checkFavouriteLocation(locationId: number): Promise<any> {
		// Use the correct endpoint that handles locationId - /isMyFavourite/:dentistId uses dentistId, not locationId
		return this.get(`v3/hygienists/favouriteDentist/${locationId}`)
	}

	checkFavouriteHygienist(data: {hygienistId: number, locationId: number}): Promise<any> {
		return this.get(`v3/dentists/isMyFavourite/${data.hygienistId}/${data.locationId}`)
	}

	checkFavouriteHygienistLocations(locationIds: number[]): Promise<any> {
		return this.post(`v3/hygienists/favouriteHygienistLocations`, { locationIds })
	}

	getResumeData(data: {hygienistId: number, locationId: number, jobId?: number, offerId?: number}): Promise<any> {
		const context = [
			data.jobId ? `jobId=${data.jobId}` : '',
			data.offerId ? `offerId=${data.offerId}` : '',
		].filter(Boolean).join('&');
		return this.get(`v3/hygienists/resume/${data.hygienistId}/${data.locationId}${context ? `?${context}` : ''}`)
	}

	removePartialOffer(offerId: number): Promise<any> {
		return this.delete(`v3/offers/${offerId}`)
	}

	hygienistCancelJob(
		jobId: number,
		body?: { reason?: string; details?: string },
	): Promise<{ hygienistCancelledId?: number }> {
		return this.post(`v3/jobs/${jobId}/cancel`, body || {})
	}

	dentistCancelJob(
		jobId: number,
		body?: { reason?: string; details?: string; repostShift?: boolean },
	): Promise<{dentistCancelledId: number; supportEmailSent?: boolean; onBehalfHandled?: boolean}> {
		return this.delete(`v3/jobs/${jobId}`, body || {})
	}

	updateCancelled(data: {hygienistCancelledId: number, reason: string, details: string}): Promise<any> {
		return this.put(`v3/jobs/updateHygienistCancelled`, data)
	}

	updateDentistCancelled(data: {dentistCancelledId: number, reason: string, details: string, repostShift?: boolean}): Promise<any> {
		return this.put(`v3/jobs/updateDentistCancelled`, data)
	}

	async getAiAssistsLeft(): Promise<any> {
		return this.post('v3/textFilter/rewritesLeft')
	}

	requestWritingAssist(request: WritingAssistRequest): Promise<WritingAssistResponse> {
		return this.post('v3/ai/writing-assist', request)
	}

	getInvoiceForJob(jobId: number): Promise<InvoiceProps> {
		return this.get('v3/jobs/' + jobId + '/invoice');
	}

	/**
	 * Download binary PDF: Placement Fee Invoice
	 */
	async downloadPlacementFeeInvoice(jobId: number): Promise<Blob> {
		const url = `${this.baseUrl}v3/jobs/${jobId}/placement-fee-invoice`;
		console.debug('[Api] GET placement-fee-invoice', { url, jobId });
		const res = await fetch(url, {
			credentials: 'include',
			mode: 'cors',
			headers: {
				'Authorization': this.authorizationHeader,
				'Accept': 'application/pdf'
			}
		});
		console.debug('[Api] placement-fee-invoice response', { status: res.status, contentType: res.headers.get('content-type') });
		if (!res.ok) {
			throw new ApiError(res.status, await res.text());
		}
		const blob = await res.blob();
		console.debug('[Api] placement-fee-invoice blob', { size: (blob as any)?.size });
		if (!blob || (blob as any).size === 0) {
			throw new ApiError(500, 'Empty PDF response');
		}
		return blob;
	}

	/**
	 * Download binary PDF: Talent Invoice (Hygienist/Assistant)
	 */
	async downloadTalentInvoice(jobId: number): Promise<Blob> {
		const url = `${this.baseUrl}v3/jobs/${jobId}/talent-invoice`;
		console.debug('[Api] GET talent-invoice', { url, jobId });
		const res = await fetch(url, {
			credentials: 'include',
			mode: 'cors',
			headers: {
				'Authorization': this.authorizationHeader,
				'Accept': 'application/pdf'
			}
		});
		console.debug('[Api] talent-invoice response', { status: res.status, contentType: res.headers.get('content-type') });
		if (!res.ok) {
			throw new ApiError(res.status, await res.text());
		}
		const blob = await res.blob();
		console.debug('[Api] talent-invoice blob', { size: (blob as any)?.size });
		if (!blob || (blob as any).size === 0) {
			throw new ApiError(500, 'Empty PDF response');
		}
		return blob;
	}

	/**
	 * Fetch all invoices visible to the logged-in hygienist.
	 * Returns an array of jobs, each possibly containing an `invoice` property.
	 */
	getInvoices(): Promise<any[]> {
		return this.get('v3/invoices');
	}

	/**
	 * Download binary PDF: Latest invoice by invoiceId for owning hygienist
	 */
	async downloadInvoicePdf(invoiceId: number): Promise<Blob> {
		const url = `${this.baseUrl}v3/invoices/${invoiceId}/pdf`;
		const res = await fetch(url, {
			credentials: 'include',
			mode: 'cors',
			headers: {
				'Authorization': this.authorizationHeader,
				'Accept': 'application/pdf',
			},
		});
		if (!res.ok) {
			throw new ApiError(res.status, await res.text());
		}
		const blob = await res.blob();
		if (!blob || (blob as any).size === 0) {
			throw new ApiError(500, 'Empty PDF response');
		}
		return blob;
	}

	payInvoice(invoiceId: number, expectedQuote?: InvoicePayNowQuote): Promise<{
		paymentLifecycleId: number;
		officePaymentStatus: 'paid';
		talentPaymentStatus: 'not_started' | 'processing' | 'action_required' | 'paid';
		talentPaymentMethod: 'instant' | 'standard' | null;
	}> {
		return this.post('v3/invoices/' + invoiceId + '/pay', expectedQuote ? { expectedQuote } : undefined);
	}

	updateAlreadyPaidInvoice(data: {
		invoiceId: number;
		paidMethod: 'e-transfer' | 'cheque' | 'direct deposit' | 'PayPal' | 'other';
		paidNote?: string;
	}): Promise<any> {
		return this.post('v3/invoices/alreadyPaid', data);
	}

	getInvoiceInformation(invoiceId: number): Promise<InvoiceInformationResponse> {
		return this.request(`v3/invoice-information/${invoiceId}`, { method: 'GET', cache: 'no-store' });
	}

	getInvoiceDispute(invoiceId: number): Promise<InvoiceDisputeContext> {
		return this.request(`v3/invoiceDisputes/invoice/${invoiceId}`, { method: 'GET', cache: 'no-store' });
	}

	getInvoiceDisputeById(disputeId: number): Promise<InvoiceDispute> {
		return this.request(`v3/invoiceDisputes/${disputeId}`, { method: 'GET', cache: 'no-store' });
	}

	reviewInvoiceDispute(invoiceId: number, body: InvoiceDisputeProposal): Promise<InvoiceDisputeReview> {
		return this.post(`v3/invoiceDisputes/invoice/${invoiceId}/review`, body);
	}

	openInvoiceDispute(invoiceId: number, body: { reviewId: number; reviewToken: string; confirmed: true }): Promise<InvoiceDisputeReceipt> {
		return this.post(`v3/invoiceDisputes/invoice/${invoiceId}`, body);
	}

	acceptInvoiceDispute(disputeId: number, body: { confirmed: true; expectedTotalCents: number; sourceHash: string }): Promise<InvoiceDisputeReceipt> {
		return this.post(`v3/invoiceDisputes/${disputeId}/accept`, body);
	}

	declineInvoiceDispute(disputeId: number): Promise<InvoiceDisputeReceipt> {
		return this.post(`v3/invoiceDisputes/${disputeId}/decline`, {});
	}

	withdrawInvoiceDispute(disputeId: number): Promise<InvoiceDisputeReceipt> {
		return this.post(`v3/invoiceDisputes/${disputeId}/withdraw`, {});
	}

	requestInvoiceInformation(invoiceId: number, item: InvoiceInformationItem): Promise<InvoiceInformationMutation> {
		return this.post(`v3/invoice-information/${invoiceId}`, { item });
	}

	renewInvoiceInformation(invoiceId: number, generation: number): Promise<InvoiceInformationMutation> {
		return this.post(`v3/invoice-information/${invoiceId}/renew`, { generation });
	}

	shareInvoiceInformation(invoiceId: number, generation: number, paymentEmail: string): Promise<InvoiceInformationMutation> {
		return this.post(`v3/invoice-information/${invoiceId}/share`, { generation, paymentEmail });
	}

	acknowledgeInvoiceInformation(invoiceId: number, generation: number): Promise<InvoiceInformationMutation> {
		return this.post(`v3/invoice-information/${invoiceId}/acknowledge`, { generation });
	}

	reportNonPayment(invoiceId: number): Promise<any> {
		return this.post(`v3/invoices/${invoiceId}/reportNonPayment`, {});
	}

	escalateNonPayment(invoiceId: number): Promise<any> {
		return this.post(`v3/invoices/${invoiceId}/escalateNonPayment`, {});
	}

	modifyJob({
		jobId,
		postedStart,
		postedEnd,
		firstPatientTime,
	}: {
		jobId: number;
		postedStart: string;
		postedEnd: string;
		firstPatientTime?: string | null;
	}): Promise<any> {
		const body: {
			postedStart: string;
			postedEnd: string;
			firstPatientTime?: string | null;
		} = { postedStart, postedEnd };
		// Include when provided (including null clear) so Modify Shift FPT edits are not dropped.
		if (firstPatientTime !== undefined) {
			body.firstPatientTime = firstPatientTime;
		}
		return this.put(`v3/jobs/${jobId}`, body);
	}

	/** T2-5707: DO updates Job.proposedRate on posted/unfilled shifts */
	updateProposedRate({ jobId, proposedRate }: { jobId: number; proposedRate: number }): Promise<{ proposedRate: number }> {
		return this.put(`v3/jobs/${jobId}/proposedRate`, { proposedRate });
	}

	getPartialOffer({offerId}: {offerId: number}): Promise<any> {
		return this.get(`v3/offers/${offerId}`)
	}

	updatePartialOfferViews({offerId}: { offerId: number }): Promise<any> {
		return this.post(`v3/offers/${offerId}/updateViews`);
	}
	
	acceptPartialOffer(offerId: number): Promise<any> {
		return this.put(`v3/offers/${offerId}/accept`);
	}
	
	rejectPartialOffer(offerId: number, data:object): Promise<any> {
		return this.put(`v3/offers/${offerId}/reject`, data);
	}
	
	removeUnbookedJob(jobId: number): Promise<any> {
		// The additive route fails closed on an older API. Never fall back to
		// generic cancellation: the shift may have been booked in the meantime.
		return this.delete(`v3/jobs/${jobId}/unbooked`, {});
	}

	removeJob(jobId: number): Promise<any> {
		return this.delete(`v3/jobs/${jobId}`, {});
	}
	
	getHygienistJobs(filter: string): Promise<any> {
		return this.get(`v3/jobs/hygienistJobs${filter ? '?filter=' + filter : ''}`);
	}

	getHygienistOffers(): Promise<any> {
		return this.get(`v3/offers/activeForHygienist`);
	}

	/**
	 * Hygienist job board available count for Job Board badge
	 */
	getAvailableHireJobsCount(): Promise<{ result: number }> {
		return this.get(`v3/hireJobs/hygienistAvailableCount`);
	}

	getResumesExpirationDate(): Promise<string> {
		return this.get(`v3/resumesExpirationDate`);
	}

  /**
   * Insta-Book explainer video URL
   */
  async getInstabookVideoLink(): Promise<string> {
    const res: any = await this.get('v3/instabookVideoLink');
    // Some backends wrap in { url } or return string directly
    return typeof res === 'string' ? res : (res?.url || res?.link || '');
  }

  /**
   * Mark a completed job to create/send invoice later (suppresses repeat prompts)
   */
  async createInvoiceLater(jobId: number): Promise<any> {
    return this.put(`v3/jobs/${jobId}/invoiceLater`);
  }
	
	requestResubmitOffer({offerId}: { offerId: number }): Promise<any> {
		return this.post(`v3/offers/${offerId}/requestResubmit`);
	}

	resetPassword(data: {password: string, token: string}): Promise<any> {
		return this.post('v3/tsusers/resetPassword', data);
	}

	contactSupport(data: any): Promise<any> {
		return this.post('v3/tsusers/support', data);
	}

	getCancellationStats({hygienistId}: {hygienistId: number}): Promise<BasicHygienistScoreProps> {
		return this.get(`v3/hygienists/cancellationStats/${hygienistId}`);
	}

	calculateCancellationStats({jobId}: {jobId: number}): Promise<HygienistScoreProps> {
		return this.post(`v3/hygienists/cancellationStats`, {jobId});
	}
	
	sendInvoice(data: any): Promise<any> {
		return this.post('v3/invoices', data);
	}

	getHygienistPersonalStats(): Promise<any> {
		return this.get('v3/hygienists/me/personalStats');
	}

	getHygienistStats(): Promise<any> {
		return this.get(`v3/hygienists/reliabilityScorePosition`);
  }

	// Unified My Stats endpoint (provisional + non-provisional)
	getMyStats(): Promise<any> {
		return this.get('v3/my/stats');
	}

  // Courses API — lmsCourseId is the TalentLMS course ID used for all endpoints
  async getCourses(): Promise<Array<{ lmsCourseId: number; title: string; description: string | null; progressPercent: number | null; isCompleted: number; passedFinalQuiz: number }>> {
    return this.get('v3/courses');
  }

  async enrollCourse(lmsCourseId: number): Promise<void> {
    await this.post('v3/courses/enroll', { lmsCourseId });
  }

  async getCourseProgress(lmsCourseId: number): Promise<{ percent: number | null; isCompleted: boolean; passedFinalQuiz: boolean; score?: number; restrictionLifted?: boolean; membershipRecoveryTransition?: { transitioned: boolean; transitionedLanes?: string[] } }> {
    return this.get(`v3/courses/${lmsCourseId}/progress`);
  }

  async getCourseLaunchUrl(lmsCourseId: number): Promise<{ url: string; courseUrl?: string; courseEntryUrl?: string; launchViaAutologin?: boolean; directUrl?: string; embedAllowed?: boolean; reason?: string; diagnostics?: Array<{ step: string; ok: boolean; code?: string; message?: string }> }> {
    return this.get(`v3/courses/${lmsCourseId}/launch`);
  }
  
  async getCoursesEmbedHealth(): Promise<{ customDomain: boolean; frameAncestorsOK: boolean; noXFO: boolean; cookiesOK: boolean; message: string }> {
    return this.get('v3/courses/health/embed');
  }
  
	resendInvoice(data: any): Promise<any> {
		return this.post(`v3/invoices/${data.id}/resend`, data);
	}

	updateInvoice(data: any): Promise<any> {
		return this.put(`v3/invoices/${data.id}`, data);
	}

	async getLastFeedbackForLocationAndHygienist(locationId: string | number, hygienistId: string | number): Promise<any> {
		return this.get(`v3/jobs/feedback/last/${locationId}/${hygienistId}`);
	}

	/** T2-6185: load office→talent feedback for a completed shift (edit hydrate). */
	getDentistFeedback(jobId: number): Promise<any> {
		return this.get(`v3/jobs/${jobId}/feedback`)
			.then((response: any) => response?.data ?? response);
	}

	saveFeedback(data: any): Promise<any> {
		return this.post('v3/jobs/feedback', data);
	}

	/** T2-6899: record review-solicitation outcome (shown/clicked/not_now/never). */
	postReviewSolicitationOutcome(body: {
		solicitationId: string;
		action: 'shown' | 'clicked' | 'not_now' | 'never';
		channel?: 'appstore' | 'play' | 'google' | null;
		jobId?: number | null;
	}): Promise<any> {
		return this.post('v3/review-solicitations', body);
	}

	/** T2-6185: update existing DO feedback (same payload as saveFeedback + isEdit). */
	updateDentistFeedback(data: any): Promise<any> {
		const body = data?.data
			? { data: { ...data.data, isEdit: true } }
			: { data: { ...data, isEdit: true } };
		return this.put('v3/jobs/feedback', body);
	}

	skipFeedbackSurvey({ jobId }: { jobId: number }): Promise<any> {
		return this.post(`v3/jobs/${jobId}/skipSurvey`, {});
	}
	
	negativeFeedback(data: any): Promise<any> {
		return this.post(`v3/jobs/${data.job.id}/negativeFeedback`, data)
	}
	
	positiveFeedback(data: any): Promise<any> {
		return this.post(`v3/jobs/${data.job.id}/positiveFeedback`, data)
	}

	// New talent office review methods
	submitTalentReview(reviewData: ReviewData): Promise<TalentReview & { reviewAsk?: any }> {
		return this.post('v3/hygienists/review', reviewData)
			.then(response => {
				const data = response?.data ?? response;
				return {
					...data,
					...(response?.reviewAsk ? { reviewAsk: response.reviewAsk } : {}),
					...(response?.supportDelivery ? { supportDelivery: response.supportDelivery } : {}),
				};
			});
	}
	
	getTalentReview(jobId: number): Promise<TalentReview> {
		return this.request(`v3/hygienists/review/${jobId}`, { method: 'GET', cache: 'no-store' })
			.then(response => response?.data ?? response);
	}

	skipTalentReview({ jobId }: { jobId: number }): Promise<any> {
		return this.post(`v3/hygienists/review/skip/${jobId}`, {})
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	}
	
	dismissTalentReview(jobId: number): Promise<any> {
		return this.post(`v3/hygienists/review/dismiss/${jobId}`)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	}
	
	async getPendingTalentReviews(): Promise<PendingReview[]> {
		try {
			// Bust caches to avoid 304 Not Modified which can break downstream logic
			const res: any = await this.request(
				'v3/hygienists/pending-reviews?_=' + Date.now(),
				{ method: 'GET', cache: 'no-store' } as RequestInit
			);
			if (Array.isArray(res)) return res as PendingReview[];
			if (res && Array.isArray(res.data)) return res.data as PendingReview[];
			return [];
		} catch (error) {
			// Fallback: try once more without relying on previous cache
			try {
				const res2: any = await this.request(
					'v3/hygienists/pending-reviews?_=' + (Date.now() + 1),
					{ method: 'GET', cache: 'no-store' } as RequestInit
				);
				if (Array.isArray(res2)) return res2 as PendingReview[];
				if (res2 && Array.isArray(res2.data)) return res2.data as PendingReview[];
				return [];
			} catch (err2) {
				return [];
			}
		}
	}

	// Renamed from getJobApplicationCandidateProfile and changed to call new GET preview endpoint
	async getJobApplicationPreviewProfile(data: { applicationId: number }): Promise<ProfilePreviewResponse> { 
		return this.get(`v3/jobApplications/${data.applicationId}/profilePreview`);
	}

	// This function was already purchaseResume, just ensuring name consistency and return type
	async purchaseJobApplicationResume(data: { applicationId: number }): Promise<FullProfileData> {
		return this.put(`v3/jobApplications/${data.applicationId}/viewResume`);
	}

	async getNotifications(params?: { limit?: number; offset?: number; hoursBack?: number }) {
		const query = new URLSearchParams();
		if (params?.limit != null) query.set('limit', String(params.limit));
		if (params?.offset != null) query.set('offset', String(params.offset));
		if (params?.hoursBack != null) query.set('hoursBack', String(params.hoursBack));
		const qs = query.toString();
		return this.get(`v3/notifications${qs ? `?${qs}` : ''}`);
	}

	async getUnreadNotificationsCount() {
		return this.get('v3/notifications/unread-count');
	}

	async markNotificationRead(notificationId: number) {
		return this.put(`v3/notifications/${notificationId}/read`);
	}

	async markAllNotificationsRead() {
		return this.put('v3/notifications/read-all');
	}

	async checkShiftsAvailableForDate(date: string): Promise<{ hasShifts: boolean }> {
		return this.get(`v3/notifications/shifts-available?date=${date}`);
	}

	async snoozeNotification(notificationId: number, durationMinutes: number = 60) {
		return this.put(`v3/notifications/${notificationId}/snooze`, { durationMinutes });
	}

	async unhideJobs(): Promise<any> {
		return this.put('v3/hygienists/jobs/unhide')
	}

	getUserFilters(): Promise<{ activeFilters: FiltersProps, previousFilters: FiltersProps | null }> {
		return this.get('v3/hygienists/filters')
	}

	saveUserFilters(filters: { activeFilters?: FiltersProps | null, previousFilters?: FiltersProps | null }): Promise<any> {
		return this.post('v3/hygienists/filters', filters)
	}

	/** Check if the logged-in hygienist still needs to add languages */
	async getNeedsLanguages(): Promise<{ needsLanguages: boolean }> {
		return this.get('v3/tsusers/me/needsLanguages');
	}

	async dismissLanguagesModal(): Promise<any> {
		return this.post('v3/tsusers/me/needsLanguages/dismiss');
	}

	async checkTextContactInfo(message: string, context: string = 'aboutYou', isJobApplication: boolean = false): Promise<any> {
		return this.post('v3/textFilter', { message, context, isJobApplication });
	}
	async initiateSignup(data: any): Promise<any> {
		return this.post('v3/tsusers/initiate-signup', data);
	}

	async completeRoleSelection(data: { role: string }): Promise<any> {
		return this.post('v3/tsusers/complete-role-selection', data);
	}

	async getJob(jobId: number): Promise<any> {
		return this.request(`v3/jobs/${jobId}`, { method: 'GET' });
	}

	// Job Completed Page API methods - only adding methods that don't already exist
	async updateJobNotes(props: { jobId: number; notes: string }): Promise<void> {
		return this.put(`v3/jobs/${props.jobId}/update`, { dentistPrivateNotes: props.notes });
	}
	/**
	 * Update hygienist private notes for a job (Completed Shift page - Hygienist view)
	 */
	async updateJobHygienistNotes(props: { jobId: number; notes: string }): Promise<void> {
		return this.put(`v3/jobs/${props.jobId}/update`, { hygienistPrivateNotes: props.notes });
	}

	async getLastJobRate(locationId: number): Promise<{ hourlyRate: number }> {
		return this.get(`v3/jobs/lastRate/${locationId}`);
	}

  // -------- Boosted Posts (T2-4286) --------
  async getBoostAvailability(jobId: number): Promise<{ status: 'available' | 'queued' | 'unavailable'; activeCount: number; roleType: 0 | 1; }> {
    return this.get(`v3/jobs/${jobId}/boost/availability`);
  }

  async boostShift(jobId: number): Promise<{ status: 'in_rotation' | 'queued'; message: string; position?: number; boostedPostId?: number; }> {
    return this.post(`v3/jobs/${jobId}/boost`);
  }

  async getBoostStatus(jobId: number): Promise<{ isBoosted: boolean; status: string; }> {
    return this.get(`v3/jobs/${jobId}/boost/status`);
  }

  async turnOffBoost(jobId: number): Promise<{ status: 'turned_off'; }> {
    return this.post(`v3/jobs/${jobId}/boost/turn-off`);
  }
  
  // NOTE: pre-post selection (boolean) is carried to confirmation optimizer; activation occurs after posting
	
	async getManualBookShiftPageData(): Promise<{
		offices: Array<{
			id: number;
			practiceName: string;
			address: string;
			city: string;
			dentistId: number;
			lastHourlyRate?: number;
			stripeCustomerId?: string;
			isCentralBooked?: number;
		}>;
		hourlyRates: number[];
		constraints: {
			minRate: number;
			maxRate: number;
		};
		hygienist: {
			country: number;
			province?: string;
			type: number;
			blockedUntil?: string | null;
			firstName?: string;
			lastName?: string;
			timeZone?: string | null;
		};
		error?: string;
	}> {
		return this.get('v3/hygienists/manualBookShiftPageData');
	}

	/**
	 * @throws {ApiError<ManualBookShiftError>}
	 */
	async manuallyBookShift(payload: ManualShiftPayload): Promise<ManualShiftResponse> {
		const idempotencyKey = getOrCreateCreateRequestKey('manual-shift', payload);
		return this.request('v3/shifts/manuallyBookShift', {
			method: 'POST',
			headers: { 'X-Idempotency-Key': idempotencyKey },
			body: JSON.stringify(payload),
		}).then((response) => {
			clearCreateRequestKey('manual-shift', payload);
			return response as ManualShiftResponse;
		});
	}

	async checkStripeAccountStatus(): Promise<{
		isActive: boolean;
		status: string;
		message: string;
		payoutsEnabled: boolean;
		requirementsDue: string[];
		accountId?: string;
		error?: string;
	}> {
		return this.get('v3/hygienists/me/stripeAccountStatus');
	}

	async getStripeAccountLoginLink(): Promise<{ url: string }> {
		return this.post('v3/hygienists/me/stripeLink');
	}

	// ----------------- Completed Job Page -----------------
	/**
	 * BFF endpoint: gather data for the Hygienist Completed Job page
	 */
	async getCompletedJobPageData(jobId: number): Promise<any> {
		return this.get(`v3/hygienists/completedJobPageData?jobId=${jobId}`);
	}

	// ----------------- Mixpanel Audit Logs (Test Accounts Only) -----------------
	/**
	 * Get Mixpanel audit status (test accounts only)
	 */
	async getMixpanelAuditStatus(): Promise<any> {
		return this.get('v3/audit/mixpanel/status');
	}

	/**
	 * Get live Mixpanel audit logs (queries Mixpanel API directly)
	 * Test accounts only
	 */
	async getMixpanelLogsLive(queryParams?: string): Promise<any> {
		const url = queryParams ? `v3/audit/mixpanel/live?${queryParams}` : 'v3/audit/mixpanel/live';
		return this.get(url);
	}

	/**
	 * Get the minimum required version from the API
	 * Used for automatic refresh when UI is outdated
	 */
	async getMinVersion(): Promise<{
		version: string;
		minVersion: string;
		currentVersion: string;
		minimumVersions?: { ios?: string; android?: string };
	}> {
		return this.get('v3/minVersion');
	}

	// ----------------- Rate Selector Configuration -----------------
	/**
	 * Get rate selector configuration parameters.
	 * Returns configurable values for personalized rate ranges.
	 * This is a public endpoint (no auth required).
	 */
	async getRateSelectorConfig(): Promise<RateSelectorConfigResponse> {
		return this.get('v3/rate-selector-config');
	}

	// ----------------- Rate Benchmarks -----------------

	async getRateBenchmarks(params: {
		jobType: number;
		province?: string;
		postalPrefix?: string;
		locationId?: number;
		shiftDate?: string;
		shiftStartTime?: string;
	}): Promise<RateBenchmarkResponse> {
		const queryParams = new URLSearchParams({
			jobType: String(params.jobType),
		});
		if (params.locationId !== undefined && params.shiftDate && params.shiftStartTime) {
			queryParams.set('locationId', String(params.locationId));
			queryParams.set('shiftDate', params.shiftDate);
			queryParams.set('shiftStartTime', params.shiftStartTime);
		} else {
			if (!params.province || !params.postalPrefix) {
				throw new Error('province and postalPrefix are required in legacy rate benchmark mode');
			}
			queryParams.set('province', params.province);
			queryParams.set('postalPrefix', params.postalPrefix);
		}
		const qs = queryParams.toString();
		return this.get(`v3/rate-benchmarks?${qs}`);
	}
}
